import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { SAGE_ROUTE_NAME } from "@rabbit/config/enums";

type AgreementViewProps = {
  /**
   * Defaults to true. When false, hides the top "Platform Services Agreement" heading
   * that is shown on public (logged-out) routes.
   */
  showHeading?: boolean;
};

function PlatformServicesAgreementView({ showHeading = true }: AgreementViewProps) {
  const contextValues = useContext(UserContext);
  
  return (
    <div className="flex flex-grow flex-col justify-start">
      {showHeading && !contextValues?.user && <div className='font-caudex text-3xl text-primary-900 mb-4'>Platform Services Agreement</div>}
      <div className="reset-ordered-list font-nunito">
        <div>
          <ul>
            <li><strong><strong>Background </strong><span style={{fontWeight: 400}}><br /><br /></span></strong></li>
          </ul>
          <p>&nbsp;</p>
          <ol>
            <ol>
              <li style={{fontWeight: 400}}><span style={{fontWeight: 400}}>This platform services agreement (“</span><strong>Agreement</strong><span style={{fontWeight: 400}}>”) governs your purchase and use of the iWarranty services.&nbsp;</span></li>
            </ol>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><span style={{fontWeight: 400}}>By accepting this Agreement by (a) executing an order form that references this Agreement, (b) clicking a box indicating you accept this Agreement on our website or portal or (c) otherwise using our services, you as the customer agree to the terms of this Agreement.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><span style={{fontWeight: 400}}>If you are an individual accepting this Agreement on behalf of a company or other legal entity, you&nbsp; represent that you have the authority to bind such entity and its affiliates to these terms and conditions, in which case the term “Customer” shall refer to such entity and its affiliates.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><span style={{fontWeight: 400}}>This Agreement is effective as of the date of the you accepting this Agreement (the “</span><strong>Effective Date</strong><span style={{fontWeight: 400}}>”).</span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><span style={{fontWeight: 400}}>iWarranty may amend this Agreement from time to time. Amendments will be effective upon iWarranty’s posting of such updated Agreement at this location. Your continued access or use of the Services after such posting constitutes your consent to be bound by the Agreement, as amended.</span></li>
          </ol>
          <p><br /><br /></p>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Services</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>iWarranty Services</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>iWarranty will make the Services and Content available to you pursuant to this Agreement and the applicable Order Forms. In so doing, iWarranty shall: (a) provide iWarranty standard support for Services at no additional charge or upgraded support if purchased; (b) use commercially reasonable efforts to make the online Services available except for: (i) planned downtime (of which iWarranty shall give at least 8 hours electronic notice and which iWarranty shall schedule to the extent practicable during the weekend hours between 6:00 p.m. Friday and 5:00 a.m. Monday GMT), and (ii) any unavailability caused by circumstances beyond iWarranty’s reasonable control, including, for example, an act of God, act of government, flood, fire, earthquake, civil unrest, act of terror, pandemic, strike or other labour problem, internet service provider failure or delay, Non-iWarranty Applications, or denial of service attack, and (c) provide the Services in accordance with applicable laws and government regulations when used according to this Agreement and the Documentation. </span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Customer Responsibilities </strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>Customer will: (a) be responsible for its Users’ compliance with this Agreement and the Order Form, (b) be responsible for the accuracy, quality and legality of Customer Data and the means by which Customer acquired, processed and shared Customer Data, (c) use commercially reasonable efforts to prevent unauthorised access to or use of Services and Content, and notify iWarranty promptly of any such unauthorised access or use, (d) use Services and Content only in accordance with the Documentation and applicable laws and government regulations, and (e) comply with terms of service of Non-iWarranty Applications with which Customer uses Services or Content.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Usage Limits and Restrictions</strong><strong><br /></strong><strong><br /></strong><span style={{fontWeight: 400}}>The Services and Content are subject to usage limits specified in Order Forms. Unless otherwise specified, (a) where the Order Form specifies a quantity of or limit to the number of Users, the Service or Content may not be accessed by more than that number of Users, (b) a User’s password may not be shared with any other individual, and (c) a User identification may be reassigned to a new individual replacing one who no longer requires ongoing use of the Service or Content. </span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>Customer will not: (a) make any Service or Content available to, or use any Service or Content for the benefit of, anyone other than Customer or Users, (b) sell, resell, licence, sublicence, distribute, rent or lease any Service or Content, or include any Service or Content in a service bureau or outsourcing offering, (c) use a Service to store or transmit infringing, libellous, or otherwise unlawful or tortuous material, or to store or transmit material in violation of third-party privacy rights, (d) use a Service to store or transmit Malicious Code, (e) interfere with or disrupt the integrity or performance of any Service or third-party data contained therein, (f) attempt to gain unauthorised access to any Service or Content or its related systems or networks, (g) permit direct or indirect access to or use of any Service or Content in a way that circumvents a contractual usage limit, (h) copy a Service or any part, feature, function or user interface thereof, (i) copy Content except as permitted herein or in an Order Form or the Documentation, (j) frame or mirror any part of any Service or Content, other than framing on Customer’s own intranets or otherwise for its own internal business purposes or as permitted in the Documentation, (k) access any Service or Content in order to build a competitive product or service, or (l) reverse engineer any Service (to the extent such restriction is permitted by law).</span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Removal of Content and Non-iWarranty Applications</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>If iWarranty is required by a licensor to remove Content, or receives information that Content provided to Customer may violate applicable law or third-party rights, iWarranty may notify Customer and in such event Customer will promptly remove such Content from its systems.&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Duration</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Duration</strong><span style={{fontWeight: 400}}> – The agreement commences on the date of acceptance in accordance with clause 1.2 above or as otherwise specified in the Order Form(s) and continues until all subscriptions have expired or have been terminated, unless terminated earlier in accordance with its terms. Except as otherwise expressly specified in an Order Form, subscriptions will automatically renew for additional periods equal to the expiring subscription term or one year (whichever is shorter), unless either Customer or iWarranty gives notice of non-renewal at least 30 days before the end of the relevant subscription term.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Termination </strong><span style={{fontWeight: 400}}>– Either you or we may end the agreement immediately by giving written notice to the other if (i) the other materially breaches it and does not remedy the breach within 30 days, (ii) the other is or appears reasonably&nbsp; likely to be unable to pay its debts or becomes insolvent or (iii) the performance of it may breach a legal or regulatory requirement. You may also choose to cancel your subscription early at your convenience on 30 day’s notice provided that we will not provide any refunds of prepaid fees or unused Subscription Fees, and you will promptly pay all unpaid fees due through the end of the Subscription Term. </span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Customer Data Portability and Deletion</strong><span style={{fontWeight: 400}}>. Upon request by Customer made within 30 days after the effective date of termination or expiration of this Agreement, iWarranty will make the Customer Data available to Customer for csv export or download or as otherwise provided in the Documentation. After such a 30-day period, iWarranty will have no obligation to maintain or provide any Customer Data, and will thereafter delete or destroy all copies of Customer Data in its systems or otherwise in its possession or control as provided in the Documentation, unless legally prohibited or otherwise needed for iWarranty’s own records.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Survival </strong><span style={{fontWeight: 400}}>– Any clause that is meant to continue to apply after termination of the agreement will do so, including but not limited to 3, 4, 5, 6, 7, 8 &amp; 9.&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Fees And Payment</strong><strong><br /><br /></strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <ol>
            <ol>
              <ol>
                <li style={{fontWeight: 400}}><strong>Fees</strong><strong><br /></strong><strong><br /></strong><span style={{fontWeight: 400}}>Customer will pay all fees specified in Order Forms which shall include fees in respect of Product Registrations, Claim Registrations and subscription licence fees. Except as otherwise specified herein or in an Order Form, payment obligations are non-cancelable and fees paid are non-refundable and quantities purchased cannot be decreased during the relevant subscription term without written agreement.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
              </ol>
            </ol>
          </ol>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Payment</strong><strong><br /></strong><strong><br /></strong><span style={{fontWeight: 400}}>Unless otherwise stated in the Order Form, invoiced fees are due&nbsp; seven (7) days from the invoice date. If any invoiced amount is not received by the due date, then without limiting iWarranty’s rights or remedies, (a) those charges may accrue late interest at the rate of 1.5% of the outstanding balance per month, or the maximum rate permitted by law, whichever is lower and (b) on 30 days further notice,&nbsp; suspend Services until such amounts are paid in full.</span><span style={{fontWeight: 400}}><br /><br /></span></strong></li>
          </ul>
          <ul>
            <li><strong>Payment Disputes</strong> <span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>iWarranty will not exercise its rights to suspend above if Customer is disputing the applicable charges reasonably and in good faith and is cooperating diligently to resolve the dispute.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
          </ul>
          <ul>
            <li><strong>Taxes </strong><strong><br /></strong><strong><br /></strong><span style={{fontWeight: 400}}>iWarranty’s fees do not include any taxes, levies, duties or similar governmental assessments of any nature, including, for example, value-added, sales, use or withholding taxes, (collectively, “</span><strong>Taxes</strong><span style={{fontWeight: 400}}>”), which shall be added where applicable. Customer is responsible for paying all Taxes associated with its purchases hereunder. If iWarranty has the legal obligation to pay or collect Taxes for which Customer is responsible under this section, iWarranty will invoice Customer and Customer will pay that amount unless Customer provides iWarranty with a valid tax exemption certificate authorised by the appropriate taxing authority. For clarity, iWarranty is solely responsible for taxes assessable against it based on its own income, property and employees.</span></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>In-app Purchases and Referral Services</strong> <span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>The iWarranty Platform has certain additional features and functionalities within the Consumer Platform that may enable you to advertise and up sell or cross sell other products or services you may offer, such as new products, replacement parts, replacement products, accessories, service plans and extended warranties “</span><strong>Consumer Platform Purchases</strong><span style={{fontWeight: 400}}>”</span><strong>)</strong><span style={{fontWeight: 400}}>, for which we charge an additional fee. These can be purchased by the consumers by clicking within the Consumer Platform through to your website or within the Consumer Platform itself. iWarranty will receive a commission for any such purchase with the rate of commission and any charges for any such additional features as set out in the relevant Order Form.</span></li>
          </ol>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Confidentiality</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Confidential information</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>Unless otherwise expressly agreed in writing, each party will use each other’s confidential information only in relation to the provision or receipt of the Services, and will not disclose it, except where required by law or regulation, in which case either party (the “</span><strong>Receiving Party</strong><span style={{fontWeight: 400}}>”) may disclose confidential information of the other party (the “</span><strong>Disclosing Party</strong><span style={{fontWeight: 400}}>”) to the extent compelled by law to do so, provided the Receiving Party gives the Disclosing Party prior notice of the compelled disclosure (to the extent legally permitted) and reasonable assistance, at the Disclosing Party’s cost, if the Disclosing Party wishes to contest the disclosure. If the Receiving Party is compelled by law to disclose the Disclosing Party’s Confidential Information as part of a civil proceeding to which the Disclosing Party is a party, and the Disclosing Party is not contesting the disclosure, the Disclosing Party will reimburse the Receiving Party for its reasonable cost of compiling and providing secure access to that Confidential Information.&nbsp; </span><span style={{fontWeight: 400}}><br /><br /></span></li>
          </ol>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Data Protection</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Compliance</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>You and we will comply with applicable data protection legislation in relation to any personal data shared under the agreement.&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Provision of personal data</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>In respect of any personal data shared between us or that we process for you, we and you assume the other party has necessary authority for the receiving party to use, process and transfer it in accordance with the agreement, and that data subjects have been given necessary information regarding its use and processing and consent to such use and processing.&nbsp;</span></li>
          </ol>
          <p><span style={{fontWeight: 400}}>&nbsp;&nbsp;</span></p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Data processing</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>Each party may process personal data only for the purposes of any of: (i) providing or receiving the Services; (ii) security, quality and risk management activities; and (iii) complying with any requirement of law, regulation or a professional body of which we or you are a member.&nbsp;&nbsp;</span></li>
          </ol>
          <p><span style={{fontWeight: 400}}>&nbsp;</span></p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Mutual assistance</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>You and we will, in relation to the personal data provided under this agreement: (i) provide reasonable assistance in complying with the rights of data subjects and any data security obligations under applicable data protection laws; (ii) notify the other party without undue delay after becoming aware of a personal data breach; and (iii) inform the other party of any relevant notices or requests.</span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Customer Data</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><strong>Customer’s Proprietary Rights</strong><strong><br /></strong><strong><br /></strong><span style={{fontWeight: 400}}>You own and retain all rights to the Customer Data. This Agreement does not grant us any ownership rights to Customer Data. You grant permission to us and our licensors to use the Customer Data only as necessary to provide the Services to you and as otherwise permitted by this Agreement. If you are using the Services on behalf of another party, then you represent and warrant that you have all sufficient and necessary rights and permissions to do so.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Limits</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>We will not use Customer Data to contact any individual or company except as you direct or otherwise permit or is otherwise permissible under our Privacy Policy or User Terms. We will use Customer Data only in order to provide the Services to you and only as permitted by applicable law, our Privacy Policy and User Terms&nbsp; and this Agreement. </span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Data Practices and Machine Learning</strong> <span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><strong>Usage Data</strong><strong><br /></strong><span style={{fontWeight: 400}}>We may collect information about you and your consumer users when you interact with the Service as permitted by the Agreement</span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><strong>Machine Learning</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>We may, as permitted by this Agreement, our User Terms and Privacy Policy, use Customer Data in an anonymized manner for machine learning or further analysis to support certain product features and functionality within the Service. You may opt-out of having your Customer Data used for machine learning purposes by emailing privacy@iwarranty.co.&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Privacy Policy</strong><strong><br /></strong><strong><br /></strong><span style={{fontWeight: 400}}>For more information on these practices, please see our Privacy Policy (</span><a href="https://iwarranty.co/platformservices"><span style={{fontWeight: 400}}>https://iwarranty.co/privacypolicy</span></a><span style={{fontWeight: 400}}>)</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Protection of Customer Data</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>The terms of the Privacy Policy are hereby incorporated by reference and will apply to the extent any Customer Data includes Personal Data. The Privacy Policy sets out how we will process Personal Data on your behalf in connection with the Services provided to you under this Agreement. We will maintain commercially appropriate administrative, physical, and technical safeguards to protect Personal Data as described in the Privacy Policy.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Regional Data Hosting</strong><strong><br /></strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>Where commercial reasonable to do so we will store your Customer Data in a specific location or geographical region (e.g. North America or Europe or Australia) as part of your subscription subject to the terms of this Agreement and our regional data hosting policies.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Customer Data Transfers</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>We and our Affiliates may transfer Customer Data (including Personal Data) to the United States in connection with the Services. To the extent we process Personal Data from the European Economic Area and/or the United Kingdom or Personal Data that is subject to the protection of European Data Protection Laws, the Standard Contractual Clauses will apply. For more information, see our Privacy Policy.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Retention, Deletion and Retrieval of Customer Data</strong><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}><br /></span><span style={{fontWeight: 400}}>For information regarding the retention and deletion of Customer Data, please see our Privacy Policy</span></li>
          </ol>
          <p><br /><br /></p>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Intellectual Property</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Brand</strong><span style={{fontWeight: 400}}> - You and we may only use the other’s name or logo as agreed in writing, except that we may use your name or logo as necessary to deliver the Services and in our marketing materials.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Ownership</strong><span style={{fontWeight: 400}}> - Any materials created by (or licenced outside the agreement to) or provided by a party (including materials derived from any such materials) shall continue to be owned by that party (or its licensors).&nbsp; Subject to the rights expressly granted hereunder, iWarranty, its Affiliates, its licensors and Content Providers reserve all rights, title and interest in and to the Services and Content, including all related intellectual property rights with rights granted to Customer to use the same as expressly set out below.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Access to and Use of Content </strong><span style={{fontWeight: 400}}>- Customer has the right to access and use the Services and applicable Content subject to the terms of applicable Order Forms, this Agreement and the Documentation. </span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Licence by Customer to Host Customer Data and Applications</strong><span style={{fontWeight: 400}}> - Customer grants iWarranty and its Affiliates a worldwide, limited-term licence to host, copy, transmit and display Customer Data, and any Non-iWarranty Applications and program code created by or for Customer using a Service, as necessary for iWarranty to provide the Services in accordance with this Agreement. Subject to the limited licences granted herein, iWarranty acquires no right, title or interest from Customer or its licensors under this in or to any such Customer Data, Non-iWarranty Application or program code.</span></li>
          </ol>
          <p><span style={{fontWeight: 400}}><br /><br /></span></p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Licence by Customer to Use Feedback &amp; Anonymised Data</strong><span style={{fontWeight: 400}}> - Customer grants to iWarranty and its Affiliates a worldwide, perpetual, irrevocable, royalty-free licence to use, distribute, disclose, and make and incorporate into its services any suggestion, enhancement request, recommendation, correction or other feedback provided by Customer or Users relating to the operation of iWarranty’s or its Affiliates’ services. Customer acknowledges and agrees that by the Customer using the Services, iWarranty will collect aggregated and anonymised data concerning usage of the Service. Customer agrees that all intellectual property rights and other rights in such aggregated and anonymised data shall belong to iWarranty and that this data may be used for its business purposes provided this data does not include any information from which particular users of the Services can be identified.</span></li>
          </ol>
          <p><span style={{fontWeight: 400}}><br /><br /></span></p>
          <ol>
            <li style={{fontWeight: 400}}><strong>IPR infringement</strong><span style={{fontWeight: 400}}> - Each party shall: ensure that any materials that it provides to the other party do not infringe any intellectual property rights of any third party, subject to the other party using those materials in accordance with the Agreement; defend that other party and its Affiliates against any claim, demand, suit or proceeding made or brought against that party and its Affiliates by a third party (a) alleging that any provided materials infringes or misappropriates such third party’s intellectual property rights, or (b) arising from (i) use of such provided materials in an unlawful manner or in violation of the Agreement, the Documentation, or Order Form.&nbsp;&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Disclaimer and Liability</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Disclaimer </strong><span style={{fontWeight: 400}}>-&nbsp; iWarranty makes no warranties of any kind and excludes all warranties, representations, terms, conditions or other commitments of any kind, whether express or implied, statutory or otherwise, and iWarranty specifically disclaims all implied warranties, including (without limitation) any warranties, representations, terms, conditions or other commitments of merchantability or fitness for a particular purpose or of satisfactory quality or of reasonable skill and care, in each case, to the maximum extent permitted by applicable law. Without prejudice to the foregoing, Content and Beta Services are provided ‘as is’ and without warranty of any kind, and each party disclaims all liability and indemnification obligations for any harm, damages or other liability caused by any third party hosting providers.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Specific types of loss</strong><span style={{fontWeight: 400}}> – You and we agree that you and we will not be liable whether in contract, tort or under any other theory of liability including without limitation for direct, indirect or consequential loss or damage, howsoever caused and whether or not such losses are foreseeable, even if that party has been advised (or is otherwise aware) of the possibility of such losses in advance.&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Liability cap – </strong><span style={{fontWeight: 400}}>Each party’s total liability (including interest) for all claims connected with this Agreement and any Order Forms (including but not limited to negligence) is limited to the amount paid by Customer to iWarranty in the 12 months immediately preceding the relevant incident, provided that in no event shall either party’s total aggregate liability arising out of or related to this Agreement and any and all Order Forms (whether in contract or tort or under any other theory of liability) exceed the total amount paid by Customer to iWarranty.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Your Products</strong><span style={{fontWeight: 400}}> - You agree that the Consumer Platform and our Services merely allows consumers to connect to and interact with you and your third parties / channel partners and we are not responsible in relation to any such interactions, repairs, warranty claims and / or&nbsp; Consumer Platform Purchases and that you are responsible for the same, including, without limitation, the sale, characteristics, and technical specifications of the Products, their quality and safety and delivery. You agree that we do not control, monitor, moderate or inspect any Products offered by you or your third parties / channel partners via the Consumer Platform and that we do not bear any responsibility in connection with such Products or Consumer Platform Purchases, including but not limited to their quality, safety, accuracy, or your ability to provide them as well as any consumers activity on the Consumer Platform, including but not limited to their legal capacity or ability to complete a transaction and pay the associated costs. You agree to indemnify us and hold us harmless from any costs, losses or claims which we may suffer or incur and which result from any interactions, repairs, warranty claims and / or&nbsp; Consumer Platform Purchases and that you are responsible for the same, including, without limitation, the sale, characteristics, and technical specifications of the Products, their quality and safety and delivery.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Unlimited liability</strong><span style={{fontWeight: 400}}> – Nothing in the agreement will limit a person’s liability for (i) death or personal injury caused by that person’s negligence, (ii) for that person’s fraud, (iii) the indemnity under clause 8.4 or (iv) for anything else that cannot by law be limited.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
          </ol>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>General&nbsp;</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Law and jurisdiction –</strong><span style={{fontWeight: 400}}> The agreement and any dispute arising from it, whether contractual or non-contractual, will be governed by English law and, following mediation, shall be referred to and finally resolved </span><span style={{fontWeight: 400}}>by arbitration under the LCIA Rules, which Rules are deemed to be incorporated by reference into this clause.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Compliance with law</strong><span style={{fontWeight: 400}}> – You and we will comply with all applicable laws (including anti-bribery, anti-corruption and data protection laws).</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Matters beyond reasonable control</strong><span style={{fontWeight: 400}}> – Neither party will be liable to the other if it fails to meet its obligations due to matters beyond its reasonable control. </span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>No obligation</strong><span style={{fontWeight: 400}}> – Neither party shall be obliged to carry out any activity in relation to the Agreement (e.g. if it believes that to do so may create a conflict of interest or may breach a legal, regulatory or professional requirement).</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Export Compliance </strong><span style={{fontWeight: 400}}>&nbsp;- the Services, Content, other iW technology, and derivatives thereof may be subject to export laws and regulations of the United States and other jurisdictions. iWarranty and Customer each represent that it is not on any U.S. government denied-party list. Customer will not permit any User to access or use any Service or Content in a U.S.-embargoed country or region (currently the Crimea, Luhansk or Donetsk regions, Cuba, Iran, North Korea, or Syria) or as may be updated from time to time on </span><a href="https://www.iwarranty.co"><span style={{fontWeight: 400}}>https://www.iwarranty.co</span></a><span style={{fontWeight: 400}}> or in violation of any U.S. export law or regulation.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Anti-Corruption</strong><span style={{fontWeight: 400}}> - Neither party has received or been offered any illegal or improper bribe, kickback, payment, gift, or thing of value from an employee or agent of the other party in connection with this Agreement. Reasonable gifts and entertainment provided in the ordinary course of business do not violate the above restriction.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Entire agreement</strong> <strong>–</strong><span style={{fontWeight: 400}}> The agreement forms the entire agreement between the parties in relation to the Services. It replaces any earlier agreements, representations and discussions regarding the Services.&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Assignment</strong><span style={{fontWeight: 400}}> – Neither party may assign, transfer or deal with their rights under the agreement without prior written consent from the other.&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Independent contractor</strong><span style={{fontWeight: 400}}> – You and we are independent contractors.&nbsp; Neither party acts as agent for the other.&nbsp; The agreement does not create a partnership (even if we use labels such as ‘partner’).&nbsp; You and we have no power to bind each other contractually, and must not suggest otherwise (impliedly or expressly) to anyone.&nbsp;&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Rights of third parties</strong><span style={{fontWeight: 400}}>&nbsp; – Except as set out in clause 7.4, a person who is not a party to the agreement has no rights under the Contracts (Rights of Third Parties) Act 1999 (as may be amended) to enforce any term of the agreement.&nbsp; The entities and individuals referred to in that clause may enforce them in their own right.&nbsp; Their consent is not required to vary or rescind the agreement.</span></li>
          </ol>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Changes</strong><span style={{fontWeight: 400}}> – Either you or we may request a change to the Agreement.&nbsp; A change will be effective only when agreed in writing.&nbsp;&nbsp;</span></li>
          </ol>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Acquisition of Non-iWarranty Products and Services</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ol>
            <li style={{fontWeight: 400}}><strong>Acquisition of Non-iWarranty Products and Services </strong><span style={{fontWeight: 400}}>- iWarranty or third parties may make available third-party products or services. Any acquisition by Customer of such non-iWarranty products or services, and any exchange of data between Customer and any non-iWarranty provider, is solely between Customer and the applicable non-iWarranty provider. iWarranty does not warrant or support Non-iWarranty Applications or other non-iWarranty products or services.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Non-iWarranty Applications and Customer Data </strong><span style={{fontWeight: 400}}>- If Customer installs or enables Non- iWarranty Applications for use with the Services, Customer acknowledges that iWarranty may allow providers of those Non-iWarranty Applications to access Customer Data as required for the interoperation and support of such Non-iWarranty Applications with the Services. Separate terms shall apply between Customer and the Non-iWarranty Application providers regarding the use of such Non-iWarranty Applications, and Customer shall be responsible for evaluating whether such terms with the Non-iWarranty Application providers ensure appropriate protection of and access to Customer Data, and address responsibility for any disclosure, modification or deletion of Customer Data by the Non-iWarranty Application providers, or any breach of applicable privacy and data protection laws and regulations resulting from Non-iWarranty Application providers’ access to Customer Data. Non-iWarranty Application providers shall not be considered subcontractors or sub-Data Processors of iWarranty or any of its Affiliates. iWarranty and its Affiliates shall not be responsible for any disclosure, modification, corruption, loss or deletion of Customer Data, or any breach of applicable privacy and data protection laws and regulations, resulting from any access by a Non-iWarranty Application provider to Customer Data through a Non-iWarranty Application installed by Customer or the Users.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Integration of Non-iWarranty Applications </strong><span style={{fontWeight: 400}}>- The Services may contain features designed to interoperate with Non-iWarranty Applications. To use such features, Customer may be required to obtain access to Non-iWarranty Applications from their providers, and may be required to grant iWarranty access to Customer’s account(s) on the Non-iWarranty Applications. If the provider of a Non-iWarranty Application ceases to make the Non-iWarranty Application available for interoperation with the corresponding Service features on reasonable terms, iWarranty may cease providing those Service features without entitling Customer to any refund, credit, or other compensation.</span><span style={{fontWeight: 400}}><br /><br /></span></li>
            <li style={{fontWeight: 400}}><strong>Removal of Content and Non-iWarranty Applications </strong><span style={{fontWeight: 400}}>-</span> <span style={{fontWeight: 400}}>If iWarranty is required by a licensor to remove Content, or receives information that Content provided to Customer may violate applicable law or third-party rights, iWarranty may so notify Customer and in such event Customer will promptly remove such Content from its systems. If iWarranty receives information that a Non-iWarranty Application hosted on a Service by Customer may violate applicable law or third-party rights, iWarranty may so notify Customer and in such event Customer will promptly disable such Non-iWarranty Application or modify the Non-iWarranty Application to resolve the potential violation. If Customer does not take required action in accordance with the above, iWarranty may disable the applicable Content, Service and/or Non-iWarranty Application until the potential violation is resolved.</span></li>
          </ol>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <ul>
            <li><strong><strong>Interpretation</strong></strong></li>
          </ul>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>In the agreement the following words and expressions have the meanings given to them below:</span></p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Agreement</strong><span style={{fontWeight: 400}}>”</span><strong> – </strong><span style={{fontWeight: 400}}>these terms and the Order Form to which they relate</span></p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Affiliate</strong><span style={{fontWeight: 400}}>” means any entity that directly or indirectly controls, is controlled by, or is under common control with the subject entity.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Beta Services</strong><span style={{fontWeight: 400}}>” means iWarranty services that are not generally available to customers.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Claim Registration</strong><span style={{fontWeight: 400}}>” means the initiation or registration of a claim or repair request or other issue in relation to a Product in the iWarranty Platform by a consumer or one of your Users.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Content</strong><span style={{fontWeight: 400}}>” means information obtained by iWarranty from its content licensors, third-party content providers, users of its consumer facing or repair network applications or publicly available sources and provided to Customer through the Services or pursuant to an Order Form, as more fully described in any relevant Documentation.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Consumer Platform</strong><span style={{fontWeight: 400}}>” means the iWarranty consumer platform or website made available to consumers to upload and manage their products and any Consumer App Purchases.</span><span style={{fontWeight: 400}}><br /><br /></span></p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Control</strong><span style={{fontWeight: 400}}>” for purposes of this definition, means direct or indirect ownership or control of more than 50% of the voting interests of the subject entity.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Customer</strong><span style={{fontWeight: 400}}>” means in the case of an individual accepting this Agreement on his or her own behalf, such individual, or in the case of an individual accepting this Agreement on behalf of a company or other legal entity, the company or other legal entity for which such individual is accepting this Agreement, and Affiliates of that company or entity (for so long as they remain Affiliates) which have entered into Order Forms. Please note that a separate Order Form is required for each Affiliate in order for that Affiliate to use the Services unless otherwise specified in writing in an Order Form.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Customer Data</strong><span style={{fontWeight: 400}}>” means electronic data and information submitted by or for Customer to the Services or collected and processed by or for Customer using the Services, excluding Content and Non-iWarranty Applications.</span><span style={{fontWeight: 400}}><br /><br /></span></p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Documentation</strong><span style={{fontWeight: 400}}>” means iWarranty’s online user guides, help and training material for the applicable Services, accessible via help.iwarranty.co or login to the applicable Services, as updated from time to time, including without limitation any notices and licence information, external usage policies, and security, privacy and architecture documentation for the applicable Services accessible through via iwarranty.co or any successor website, as updated from time to time or on login to the applicable Service.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>iWarranty</strong><span style={{fontWeight: 400}}>” means iWarranty Limited, a U.K. corporation having its principal place of business at suite 834, 19-21 Crawford Street, London W1H 1PJ, United Kingdom.</span><span style={{fontWeight: 400}}><br /><br /></span></p>
          <p><span style={{fontWeight: 400}}>“</span><strong>iWarranty Platform</strong><span style={{fontWeight: 400}}>” means iWarranty’s mobile and web-based applications (the </span><strong>“Apps”</strong><span style={{fontWeight: 400}}>), the iWarranty website (including https://iwarranty.co) (the </span><strong>“Sites”</strong><span style={{fontWeight: 400}}>) and related services, information and communications are collectively referred to as the </span><strong>“iWarranty Platform.” </strong><span style={{fontWeight: 400}}><br /><br /></span></p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Malicious Code</strong><span style={{fontWeight: 400}}>” means code, files, scripts, agents or programs intended to do harm, including, for example, viruses, worms, time bombs and Trojan horses.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Non-iWarranty Application</strong><span style={{fontWeight: 400}}>” means a Web-based or offline software application that is provided by the Customer or a third party and interoperates with a Service, including, for example, an application that is developed by or for Customer.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Order Form</strong><span style={{fontWeight: 400}}>” means an ordering or other document specifying the Services to be provided hereunder that is entered into between Customer and iWarranty, including any addenda, pilot agreements and supplements. By entering into an Order Form or using the Services, an Affiliate agrees to be bound by the terms of this Agreement as if it were an original party hereto.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Products</strong><span style={{fontWeight: 400}}>” means any and all of your products including any repairs, replacements, spare parts or new products.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Product Registration</strong><span style={{fontWeight: 400}}>” means the registration of a Product in iWarranty Platform by a consumer or one of your Users.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Product Specific Terms</strong><span style={{fontWeight: 400}}>” means standard terms from iWarranty that apply specifically to one or more particular Service, as set out in the applicable order from between the Customer and iWarranty.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>Services</strong><span style={{fontWeight: 400}}>” means the products and services that are ordered by Customer under an Order Form and made available online by iWarranty, including associated offline components, as described in the Documentation. “Services” exclude Content and Non-iWarranty Applications.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>User</strong><span style={{fontWeight: 400}}>” means, in the case of an individual accepting these terms on his or her own behalf, such individual, or, in the case of an individual accepting this Agreement on behalf of a company or other legal entity, an individual who is authorised by Customer to use a Service, for whom Customer has ordered the Service, and to whom Customer (or iWarranty at Customer’s request) has supplied a user identification and password. Users may include, for example, employees, consultants, contractors and agents of Customer, and third parties with which Customer transacts business.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>we</strong><span style={{fontWeight: 400}}>”, “</span><strong>us</strong><span style={{fontWeight: 400}}>” or “</span><strong>our</strong><span style={{fontWeight: 400}}>” refers to iWarranty Limited, a limited company incorporated in England (number 12012798) whose registered office is at Suite 834, 19-21 Crawford Street, London W1H 1PJ.</span></p>
          <p>&nbsp;</p>
          <p><span style={{fontWeight: 400}}>“</span><strong>you</strong><span style={{fontWeight: 400}}>”, “</span><strong>your</strong><span style={{fontWeight: 400}}>” means the party or parties to the agreement excluding us.</span></p>
        </div>
      </div>
    </div>
  );
}

export const PlatformServicesAgreementView1 = ({ showHeading = true }: AgreementViewProps) => {
  const contextValues = useContext(UserContext);
  return (
    <div className="flex flex-grow flex-col justify-start">
      {showHeading && !contextValues?.user && (
        <div className="mb-4 font-caudex text-3xl text-primary-900">
          Platform Services Agreement
        </div>
      )}

      <div className="reset-ordered-list psa-ordered-list font-nunito">
        <ol className="psa-ol">
          <li>
            <div className="psa-section-title">Background</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  This platform services agreement (“Agreement”) governs your
                  purchase and use of the iWarranty services.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  By accepting this Agreement by (a) executing an order form
                  that references this Agreement, (b) clicking a box indicating
                  you accept this Agreement on our website or portal or (c)
                  otherwise using our services, you as the customer agree to
                  the terms of this Agreement.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  If you are an individual accepting this Agreement on behalf
                  of a company or other legal entity, you represent that you
                  have the authority to bind such entity and its affiliates to
                  these terms and conditions, in which case the term “Customer”
                  shall refer to such entity and its affiliates.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  This Agreement is effective as of the date of the you
                  accepting this Agreement (the “Effective Date”).
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  iWarranty may amend this Agreement from time to time.
                  Amendments will be effective upon iWarranty’s posting of such
                  updated Agreement at this location. Your continued access or
                  use of the Services after such posting constitutes your
                  consent to be bound by the Agreement, as amended.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">Services</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>iWarranty Services</strong>
                </p>
                <p className="psa-paragraph">
                  iWarranty will make the Services and Content available to you
                  pursuant to this Agreement and the applicable Order Forms. In
                  so doing, iWarranty shall: (a) provide iWarranty standard
                  support for Services at no additional charge or upgraded
                  support if purchased; (b) use commercially reasonable efforts
                  to make the online Services available except for: (i) planned
                  downtime (of which iWarranty shall give at least 8 hours
                  electronic notice and which iWarranty shall schedule to the
                  extent practicable during the weekend hours between 6:00 p.m.
                  Friday and 5:00 a.m. Monday GMT), and (ii) any unavailability
                  caused by circumstances beyond iWarranty’s reasonable
                  control, including, for example, an act of God, act of
                  government, flood, fire, earthquake, civil unrest, act of
                  terror, pandemic, strike or other labour problem, internet
                  service provider failure or delay, Non-iWarranty Applications,
                  or denial of service attack, and (c) provide the Services in
                  accordance with applicable laws and government regulations
                  when used according to this Agreement and the Documentation.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Customer Responsibilities</strong>
                </p>
                <p className="psa-paragraph">
                  Customer will: (a) be responsible for its Users’ compliance
                  with this Agreement and the Order Form, (b) be responsible
                  for the accuracy, quality and legality of Customer Data and
                  the means by which Customer acquired, processed and shared
                  Customer Data, (c) use commercially reasonable efforts to
                  prevent unauthorised access to or use of Services and Content,
                  and notify iWarranty promptly of any such unauthorised access
                  or use, (d) use Services and Content only in accordance with
                  the Documentation and applicable laws and government
                  regulations, and (e) comply with terms of service of
                  Non-iWarranty Applications with which Customer uses Services
                  or Content.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Usage Limits and Restrictions</strong>
                </p>
                <p className="psa-paragraph">
                  The Services and Content are subject to usage limits specified
                  in Order Forms. Unless otherwise specified, (a) where the
                  Order Form specifies a quantity of or limit to the number of
                  Users, the Service or Content may not be accessed by more than
                  that number of Users, (b) a User’s password may not be shared
                  with any other individual, and (c) a User identification may
                  be reassigned to a new individual replacing one who no longer
                  requires ongoing use of the Service or Content.
                </p>
                <p className="psa-paragraph">
                  Customer will not: (a) make any Service or Content available
                  to, or use any Service or Content for the benefit of, anyone
                  other than Customer or Users, (b) sell, resell, licence,
                  sublicence, distribute, rent or lease any Service or Content,
                  or include any Service or Content in a service bureau or
                  outsourcing offering, (c) use a Service to store or transmit
                  infringing, libellous, or otherwise unlawful or tortuous
                  material, or to store or transmit material in violation of
                  third-party privacy rights, (d) use a Service to store or
                  transmit Malicious Code, (e) interfere with or disrupt the
                  integrity or performance of any Service or third-party data
                  contained therein, (f) attempt to gain unauthorised access to
                  any Service or Content or its related systems or networks, (g)
                  permit direct or indirect access to or use of any Service or
                  Content in a way that circumvents a contractual usage limit,
                  (h) copy a Service or any part, feature, function or user
                  interface thereof, (i) copy Content except as permitted herein
                  or in an Order Form or the Documentation, (j) frame or mirror
                  any part of any Service or Content, other than framing on
                  Customer’s own intranets or otherwise for its own internal
                  business purposes or as permitted in the Documentation, (k)
                  access any Service or Content in order to build a competitive
                  product or service, or (l) reverse engineer any Service (to
                  the extent such restriction is permitted by law).
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Removal of Content and Non-iWarranty Applications</strong>
                </p>
                <p className="psa-paragraph">
                  If iWarranty is required by a licensor to remove Content, or
                  receives information that Content provided to Customer may
                  violate applicable law or third-party rights, iWarranty may
                  notify Customer and in such event Customer will promptly
                  remove such Content from its systems.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">Duration</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>Duration</strong>
                </p>
                <p className="psa-paragraph">
                  The agreement commences on the date of acceptance in
                  accordance with clause 1.2 above or as otherwise specified in
                  the Order Form(s) and continues until all subscriptions have
                  expired or have been terminated, unless terminated earlier in
                  accordance with its terms. Except as otherwise expressly
                  specified in an Order Form, subscriptions will automatically
                  renew for additional periods equal to the expiring
                  subscription term or one year (whichever is shorter), unless
                  either Customer or iWarranty gives notice of non-renewal at
                  least 30 days before the end of the relevant subscription
                  term.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Termination</strong>
                </p>
                <p className="psa-paragraph">
                  Either you or we may end the agreement immediately by giving
                  written notice to the other if (i) the other materially
                  breaches it and does not remedy the breach within 30 days,
                  (ii) the other is or appears reasonably likely to be unable
                  to pay its debts or becomes insolvent or (iii) the
                  performance of it may breach a legal or regulatory
                  requirement. You may also choose to cancel your subscription
                  early at your convenience on 30 day’s notice provided that we
                  will not provide any refunds of prepaid fees or unused
                  Subscription Fees, and you will promptly pay all unpaid fees
                  due through the end of the Subscription Term.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Customer Data Portability and Deletion</strong>
                </p>
                <p className="psa-paragraph">
                  Upon request by Customer made within 30 days after the
                  effective date of termination or expiration of this Agreement,
                  iWarranty will make the Customer Data available to Customer
                  for csv export or download or as otherwise provided in the
                  Documentation. After such a 30-day period, iWarranty will have
                  no obligation to maintain or provide any Customer Data, and
                  will thereafter delete or destroy all copies of Customer Data
                  in its systems or otherwise in its possession or control as
                  provided in the Documentation, unless legally prohibited or
                  otherwise needed for iWarranty’s own records.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Survival</strong>
                </p>
                <p className="psa-paragraph">
                  Any clause that is meant to continue to apply after
                  termination of the agreement will do so, including but not
                  limited to 3, 4, 5, 6, 7, 8 &amp; 9.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">Fees And Payment</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>Fees</strong>
                </p>
                <p className="psa-paragraph">
                  Customer will pay all fees specified in Order Forms which
                  shall include fees in respect of Product Registrations, Claim
                  Registrations and subscription licence fees. Except as
                  otherwise specified herein or in an Order Form, payment
                  obligations are non-cancelable and fees paid are non-refundable
                  and quantities purchased cannot be decreased during the
                  relevant subscription term without written agreement.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Payment</strong>
                </p>
                <p className="psa-paragraph">
                  Unless otherwise stated in the Order Form, invoiced fees are
                  due seven (7) days from the invoice date. If any invoiced
                  amount is not received by the due date, then without limiting
                  iWarranty’s rights or remedies, (a) those charges may accrue
                  late interest at the rate of 1.5% of the outstanding balance
                  per month, or the maximum rate permitted by law, whichever is
                  lower and (b) on 30 days further notice, suspend Services
                  until such amounts are paid in full.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Payment Disputes</strong>
                </p>
                <p className="psa-paragraph">
                  iWarranty will not exercise its rights to suspend above if
                  Customer is disputing the applicable charges reasonably and in
                  good faith and is cooperating diligently to resolve the
                  dispute.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Taxes</strong>
                </p>
                <p className="psa-paragraph">
                  iWarranty’s fees do not include any taxes, levies, duties or
                  similar governmental assessments of any nature, including, for
                  example, value-added, sales, use or withholding taxes,
                  (collectively, “Taxes”), which shall be added where applicable.
                  Customer is responsible for paying all Taxes associated with
                  its purchases hereunder. If iWarranty has the legal obligation
                  to pay or collect Taxes for which Customer is responsible under
                  this section, iWarranty will invoice Customer and Customer will
                  pay that amount unless Customer provides iWarranty with a valid
                  tax exemption certificate authorised by the appropriate taxing
                  authority. For clarity, iWarranty is solely responsible for
                  taxes assessable against it based on its own income, property
                  and employees.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>In-app Purchases and Referral Services</strong>
                </p>
                <p className="psa-paragraph">
                  The iWarranty Platform has certain additional features and
                  functionalities within the Consumer Platform that may enable
                  you to advertise and up sell or cross sell other products or
                  services you may offer, such as new products, replacement
                  parts, replacement products, accessories, service plans and
                  extended warranties “Consumer Platform Purchases”), for which
                  we charge an additional fee. These can be purchased by the
                  consumers by clicking within the Consumer Platform through to
                  your website or within the Consumer Platform itself. iWarranty
                  will receive a commission for any such purchase with the rate
                  of commission and any charges for any such additional features
                  as set out in the relevant Order Form.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">Confidentiality</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>Confidential information</strong>
                </p>
                <p className="psa-paragraph">
                  Unless otherwise expressly agreed in writing, each party will
                  use each other’s confidential information only in relation to
                  the provision or receipt of the Services, and will not disclose
                  it, except where required by law or regulation, in which case
                  either party (the “Receiving Party”) may disclose confidential
                  information of the other party (the “Disclosing Party”) to the
                  extent compelled by law to do so, provided the Receiving Party
                  gives the Disclosing Party prior notice of the compelled
                  disclosure (to the extent legally permitted) and reasonable
                  assistance, at the Disclosing Party’s cost, if the Disclosing
                  Party wishes to contest the disclosure. If the Receiving Party
                  is compelled by law to disclose the Disclosing Party’s
                  Confidential Information as part of a civil proceeding to which
                  the Disclosing Party is a party, and the Disclosing Party is
                  not contesting the disclosure, the Disclosing Party will
                  reimburse the Receiving Party for its reasonable cost of
                  compiling and providing secure access to that Confidential
                  Information.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">Data Protection</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>Compliance</strong>
                </p>
                <p className="psa-paragraph">
                  You and we will comply with applicable data protection
                  legislation in relation to any personal data shared under the
                  agreement.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Provision of personal data</strong>
                </p>
                <p className="psa-paragraph">
                  In respect of any personal data shared between us or that we
                  process for you, we and you assume the other party has
                  necessary authority for the receiving party to use, process and
                  transfer it in accordance with the agreement, and that data
                  subjects have been given necessary information regarding its
                  use and processing and consent to such use and processing.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Data processing</strong>
                </p>
                <p className="psa-paragraph">
                  Each party may process personal data only for the purposes of
                  any of: (i) providing or receiving the Services; (ii) security,
                  quality and risk management activities; and (iii) complying
                  with any requirement of law, regulation or a professional body
                  of which we or you are a member.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Mutual assistance</strong>
                </p>
                <p className="psa-paragraph">
                  You and we will, in relation to the personal data provided
                  under this agreement: (i) provide reasonable assistance in
                  complying with the rights of data subjects and any data security
                  obligations under applicable data protection laws; (ii) notify
                  the other party without undue delay after becoming aware of a
                  personal data breach; and (iii) inform the other party of any
                  relevant notices or requests.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Customer Data</strong>
                </p>
                <ol>
                  <li>
                    <p className="psa-paragraph">
                      <strong>Customer’s Proprietary Rights</strong>
                    </p>
                    <p className="psa-paragraph">
                      You own and retain all rights to the Customer Data. This
                      Agreement does not grant us any ownership rights to Customer
                      Data. You grant permission to us and our licensors to use
                      the Customer Data only as necessary to provide the Services
                      to you and as otherwise permitted by this Agreement. If you
                      are using the Services on behalf of another party, then you
                      represent and warrant that you have all sufficient and
                      necessary rights and permissions to do so.
                    </p>
                  </li>
                </ol>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Limits</strong>
                </p>
                <p className="psa-paragraph">
                  We will not use Customer Data to contact any individual or
                  company except as you direct or otherwise permit or is otherwise
                  permissible under our Privacy Policy or User Terms. We will use
                  Customer Data only in order to provide the Services to you and
                  only as permitted by applicable law, our Privacy Policy and User
                  Terms and this Agreement.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Data Practices and Machine Learning</strong>
                </p>
                <p className="psa-paragraph">
                  <strong>Usage Data:</strong> We may collect information about you
                  and your consumer users when you interact with the Service as
                  permitted by the Agreement
                </p>
                <p className="psa-paragraph">
                  <strong>Machine Learning:</strong> We may, as permitted by this
                  Agreement, our User Terms and Privacy Policy, use Customer Data
                  in an anonymized manner for machine learning or further analysis
                  to support certain product features and functionality within the
                  Service. You may opt-out of having your Customer Data used for
                  machine learning purposes by emailing privacy@iwarranty.co.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Privacy Policy</strong>
                </p>
                <p className="psa-paragraph">
                  For more information on these practices, please see our Privacy
                  Policy (
                  <a
                    href="https://iwarranty.co/privacypolicy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://iwarranty.co/privacypolicy
                  </a>
                  )
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Protection of Customer Data</strong>
                </p>
                <p className="psa-paragraph">
                  The terms of the Privacy Policy are hereby incorporated by
                  reference and will apply to the extent any Customer Data includes
                  Personal Data. The Privacy Policy sets out how we will process
                  Personal Data on your behalf in connection with the Services
                  provided to you under this Agreement. We will maintain
                  commercially appropriate administrative, physical, and technical
                  safeguards to protect Personal Data as described in the Privacy
                  Policy.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Hosting and Regional Data Hosting</strong>
                </p>
                <p className="psa-paragraph">
                  Where commercial reasonable to do so we will store your Customer
                  Data in a specific location or geographical region (e.g. North
                  America or Europe or Australia) as part of your subscription
                  subject to the terms of this Agreement and our regional data
                  hosting policies.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Customer Data Transfers</strong>
                </p>
                <p className="psa-paragraph">
                  We and our Affiliates may transfer Customer Data (including
                  Personal Data) to the United States in connection with the
                  Services. To the extent we process Personal Data from the
                  European Economic Area and/or the United Kingdom or Personal
                  Data that is subject to the protection of European Data
                  Protection Laws, the Standard Contractual Clauses will apply.
                  For more information, see our Privacy Policy.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Retention, Deletion and Retrieval of Customer Data</strong>
                </p>
                <p className="psa-paragraph">
                  For information regarding the retention and deletion of Customer
                  Data, please see our Privacy Policy
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">Intellectual Property</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>Brand</strong> - You and we may only use the other’s name
                  or logo as agreed in writing, except that we may use your name
                  or logo as necessary to deliver the Services and in our marketing
                  materials.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Ownership</strong> - Any materials created by (or
                  licenced outside the agreement to) or provided by a party
                  (including materials derived from any such materials) shall
                  continue to be owned by that party (or its licensors). Subject to
                  the rights expressly granted hereunder, iWarranty, its
                  Affiliates, its licensors and Content Providers reserve all
                  rights, title and interest in and to the Services and Content,
                  including all related intellectual property rights with rights
                  granted to Customer to use the same as expressly set out below.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Access to and Use of Content</strong> - Customer has the
                  right to access and use the Services and applicable Content
                  subject to the terms of applicable Order Forms, this Agreement
                  and the Documentation.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Licence by Customer to Host Customer Data and Applications</strong>{' '}
                  - Customer grants iWarranty and its Affiliates a worldwide,
                  limited-term licence to host, copy, transmit and display Customer
                  Data, and any Non-iWarranty Applications and program code created
                  by or for Customer using a Service, as necessary for iWarranty to
                  provide the Services in accordance with this Agreement. Subject
                  to the limited licences granted herein, iWarranty acquires no
                  right, title or interest from Customer or its licensors under
                  this in or to any such Customer Data, Non-iWarranty Application
                  or program code.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Licence by Customer to Use Feedback &amp; Anonymised Data</strong>{' '}
                  - Customer grants to iWarranty and its Affiliates a worldwide,
                  perpetual, irrevocable, royalty-free licence to use, distribute,
                  disclose, and make and incorporate into its services any
                  suggestion, enhancement request, recommendation, correction or
                  other feedback provided by Customer or Users relating to the
                  operation of iWarranty’s or its Affiliates’ services. Customer
                  acknowledges and agrees that by the Customer using the Services,
                  iWarranty will collect aggregated and anonymised data concerning
                  usage of the Service. Customer agrees that all intellectual
                  property rights and other rights in such aggregated and
                  anonymised data shall belong to iWarranty and that this data may
                  be used for its business purposes provided this data does not
                  include any information from which particular users of the
                  Services can be identified.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>IPR infringement</strong> - Each party shall: ensure that
                  any materials that it provides to the other party do not
                  infringe any intellectual property rights of any third party,
                  subject to the other party using those materials in accordance
                  with the Agreement; defend that other party and its Affiliates
                  against any claim, demand, suit or proceeding made or brought
                  against that party and its Affiliates by a third party (a)
                  alleging that any provided materials infringes or misappropriates
                  such third party’s intellectual property rights, or (b) arising
                  from (i) use of such provided materials in an unlawful manner or
                  in violation of the Agreement, the Documentation, or Order Form.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">Disclaimer and Liability</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>Disclaimer</strong> - iWarranty makes no warranties of
                  any kind and excludes all warranties, representations, terms,
                  conditions or other commitments of any kind, whether express or
                  implied, statutory or otherwise, and iWarranty specifically
                  disclaims all implied warranties, including (without limitation)
                  any warranties, representations, terms, conditions or other
                  commitments of merchantability or fitness for a particular
                  purpose or of satisfactory quality or of reasonable skill and
                  care, in each case, to the maximum extent permitted by applicable
                  law. Without prejudice to the foregoing, Content and Beta
                  Services are provided ‘as is’ and without warranty of any kind,
                  and each party disclaims all liability and indemnification
                  obligations for any harm, damages or other liability caused by
                  any third party hosting providers.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Specific types of loss</strong> – You and we agree that
                  you and we will not be liable whether in contract, tort or
                  under any other theory of liability including without limitation
                  for direct, indirect or consequential loss or damage, howsoever
                  caused and whether or not such losses are foreseeable, even if
                  that party has been advised (or is otherwise aware) of the
                  possibility of such losses in advance.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Liability cap</strong> – iWarranty’s total liability
                  (including interest) for all claims connected with this
                  Agreement and any Order Forms (including but not limited to
                  negligence) is limited to the amount paid by Customer to
                  iWarranty in the 12 months immediately preceding the relevant
                  incident, provided that in no event shall either party’s total
                  aggregate liability arising out of or related to this Agreement
                  and any and all Order Forms (whether in contract or tort or
                  under any other theory of liability) exceed the total amount
                  paid by Customer to iWarranty.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Your Products</strong> - You agree that the Consumer
                  Platform and our Services merely allows consumers to connect to
                  and interact with you and your third parties / channel partners
                  and we are not responsible in relation to any such interactions,
                  repairs, warranty claims and / or Consumer Platform Purchases
                  and that you are responsible for the same, including, without
                  limitation, the sale, characteristics, and technical
                  specifications of the Products, their quality and safety and
                  delivery. You agree that we do not control, monitor, moderate or
                  inspect any Products offered by you or your third parties /
                  channel partners via the Consumer Platform and that we do not
                  bear any responsibility in connection with such Products or
                  Consumer Platform Purchases, including but not limited to their
                  quality, safety, accuracy, or your ability to provide them as
                  well as any consumers activity on the Consumer Platform,
                  including but not limited to their legal capacity or ability to
                  complete a transaction and pay the associated costs. You agree
                  to indemnify us and hold us harmless from any costs, losses or
                  claims which we may suffer or incur and which result from any
                  interactions, repairs, warranty claims and / or Consumer
                  Platform Purchases and that you are responsible for the same,
                  including, without limitation, the sale, characteristics, and
                  technical specifications of the Products, their quality and
                  safety and delivery.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Unlimited liability</strong> – Nothing in the agreement
                  will limit a person’s liability for (i) death or personal
                  injury caused by that person’s negligence, (ii) for that
                  person’s fraud, (iii) the indemnity under clause 8.4 or (iv) for
                  anything else that cannot by law be limited.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">General</div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>Law and jurisdiction</strong> – The agreement and any
                  dispute arising from it, whether contractual or non-contractual,
                  will be governed by English law and, following mediation, shall
                  be referred to and finally resolved by arbitration under the
                  LCIA Rules, which Rules are deemed to be incorporated by
                  reference into this clause.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Compliance with law</strong> – You and we will comply
                  with all applicable laws (including anti-bribery, anti-corruption
                  and data protection laws).
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Matters beyond reasonable control</strong> – Neither
                  party will be liable to the other if it fails to meet its
                  obligations due to matters beyond its reasonable control.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>No obligation</strong> – Neither party shall be obliged
                  to carry out any activity in relation to the Agreement (e.g. if
                  it believes that to do so may create a conflict of interest or
                  may breach a legal, regulatory or professional requirement).
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Export Compliance</strong> - the Services, Content, other
                  iW technology, and derivatives thereof may be subject to export
                  laws and regulations of the United States and other jurisdictions.
                  iWarranty and Customer each represent that it is not on any U.S.
                  government denied-party list. Customer will not permit any User
                  to access or use any Service or Content in a U.S.-embargoed
                  country or region (currently the Crimea, Luhansk or Donetsk
                  regions, Cuba, Iran, North Korea, or Syria) or as may be updated
                  from time to time on{' '}
                  <a href="https://www.iwarranty.co" target="_blank" rel="noreferrer">
                    https://www.iwarranty.co
                  </a>{' '}
                  or in violation of any U.S. export law or regulation.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Anti-Corruption</strong> - Neither party has received or
                  been offered any illegal or improper bribe, kickback, payment,
                  gift, or thing of value from an employee or agent of the other
                  party in connection with this Agreement. Reasonable gifts and
                  entertainment provided in the ordinary course of business do not
                  violate the above restriction.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Entire agreement</strong> – The agreement forms the entire
                  agreement between the parties in relation to the Services. It
                  replaces any earlier agreements, representations and discussions
                  regarding the Services.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Assignment</strong> – Neither party may assign, transfer
                  or deal with their rights under the agreement without prior
                  written consent from the other.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Independent contractor</strong> – You and we are
                  independent contractors. Neither party acts as agent for the
                  other. The agreement does not create a partnership (even if we
                  use labels such as ‘partner’). You and we have no power to bind
                  each other contractually, and must not suggest otherwise
                  (impliedly or expressly) to anyone.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Rights of third parties</strong> – Except as set out in
                  clause 6.4, a person who is not a party to the agreement has no
                  rights under the Contracts (Rights of Third Parties) Act 1999
                  (as may be amended) to enforce any term of the agreement. The
                  entities and individuals referred to in that clause may enforce
                  them in their own right. Their consent is not required to vary
                  or rescind the agreement.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Changes</strong> – Either you or we may request a change
                  to the Agreement. A change will be effective only when agreed in
                  writing.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">
              Acquisition of Non-iWarranty Products and Services
            </div>
            <ol>
              <li>
                <p className="psa-paragraph">
                  <strong>Acquisition of Non-iWarranty Products and Services</strong>{' '}
                  - iWarranty or third parties may make available third-party
                  products or services. Any acquisition by Customer of such
                  non-iWarranty products or services, and any exchange of data
                  between Customer and any non-iWarranty provider, is solely
                  between Customer and the applicable non-iWarranty provider.
                  iWarranty does not warrant or support Non-iWarranty Applications
                  or other non-iWarranty products or services.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Non-iWarranty Applications and Customer Data</strong> - If
                  Customer installs or enables Non- iWarranty Applications for use
                  with the Services, Customer acknowledges that iWarranty may allow
                  providers of those Non-iWarranty Applications to access Customer
                  Data as required for the interoperation and support of such
                  Non-iWarranty Applications with the Services. Separate terms shall
                  apply between Customer and the Non-iWarranty Application providers
                  regarding the use of such Non-iWarranty Applications, and Customer
                  shall be responsible for evaluating whether such terms with the
                  Non-iWarranty Application providers ensure appropriate protection
                  of and access to Customer Data, and address responsibility for
                  any disclosure, modification or deletion of Customer Data by the
                  Non-iWarranty Application providers, or any breach of applicable
                  privacy and data protection laws and regulations resulting from
                  Non-iWarranty Application providers’ access to Customer Data.
                  Non-iWarranty Application providers shall not be considered
                  subcontractors or sub-Data Processors of iWarranty or any of its
                  Affiliates. iWarranty and its Affiliates shall not be responsible
                  for any disclosure, modification, corruption, loss or deletion of
                  Customer Data, or any breach of applicable privacy and data
                  protection laws and regulations, resulting from any access by a
                  Non-iWarranty Application provider to Customer Data through a
                  Non-iWarranty Application installed by Customer or the Users.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Integration of Non-iWarranty Applications</strong> - The
                  Services may contain features designed to interoperate with
                  Non-iWarranty Applications. To use such features, Customer may be
                  required to obtain access to Non-iWarranty Applications from their
                  providers, and may be required to grant iWarranty access to
                  Customer’s account(s) on the Non-iWarranty Applications. If the
                  provider of a Non-iWarranty Application ceases to make the
                  Non-iWarranty Application available for interoperation with the
                  corresponding Service features on reasonable terms, iWarranty may
                  cease providing those Service features without entitling Customer
                  to any refund, credit, or other compensation.
                </p>
              </li>
              <li>
                <p className="psa-paragraph">
                  <strong>Removal of Content and Non-iWarranty Applications</strong> - If
                  iWarranty is required by a licensor to remove Content, or receives
                  information that Content provided to Customer may violate
                  applicable law or third-party rights, iWarranty may so notify
                  Customer and in such event Customer will promptly remove such
                  Content from its systems. If iWarranty receives information that a
                  Non-iWarranty Application hosted on a Service by Customer may
                  violate applicable law or third-party rights, iWarranty may so
                  notify Customer and in such event Customer will promptly disable
                  such Non-iWarranty Application or modify the Non-iWarranty
                  Application to resolve the potential violation. If Customer does
                  not take required action in accordance with the above, iWarranty
                  may disable the applicable Content, Service and/or Non-iWarranty
                  Application until the potential violation is resolved.
                </p>
              </li>
            </ol>
          </li>

          <li>
            <div className="psa-section-title">Interpretation</div>
            <p className="psa-paragraph">
              In the agreement the following words and expressions have the
              meanings given to them below:
            </p>

            <dl className="psa-definition-list">
              <dt>“Agreement”</dt>
              <dd>these terms and the Order Form to which they relate</dd>

              <dt>“Affiliate”</dt>
              <dd>
                means any entity that directly or indirectly controls, is
                controlled by, or is under common control with the subject
                entity.
              </dd>

              <dt>“Beta Services”</dt>
              <dd>means iWarranty services that are not generally available to customers.</dd>

              <dt>“Claim Registration”</dt>
              <dd>
                means the initiation or registration of a claim or repair request
                or other issue in relation to a Product in the iWarranty Platform
                by a consumer or one of your Users.
              </dd>

              <dt>“Content”</dt>
              <dd>
                means information obtained by iWarranty from its content licensors,
                third-party content providers, users of its consumer facing or
                repair network applications or publicly available sources and
                provided to Customer through the Services or pursuant to an Order
                Form, as more fully described in any relevant Documentation.
              </dd>

              <dt>“Consumer Platform”</dt>
              <dd>
                means the iWarranty consumer platform or website made available to
                consumers to upload and manage their products and any Consumer App
                Purchases.
              </dd>

              <dt>“Control”</dt>
              <dd>
                for purposes of this definition, means direct or indirect
                ownership or control of more than 50% of the voting interests of
                the subject entity.
              </dd>

              <dt>“Customer”</dt>
              <dd>
                means in the case of an individual accepting this Agreement on his
                or her own behalf, such individual, or in the case of an
                individual accepting this Agreement on behalf of a company or
                other legal entity, the company or other legal entity for which
                such individual is accepting this Agreement, and Affiliates of
                that company or entity (for so long as they remain Affiliates)
                which have entered into Order Forms. Please note that a separate
                Order Form is required for each Affiliate in order for that
                Affiliate to use the Services unless otherwise specified in
                writing in an Order Form.
              </dd>

              <dt>“Customer Data”</dt>
              <dd>
                means electronic data and information submitted by or for Customer
                to the Services or collected and processed by or for Customer
                using the Services, excluding Content and Non-iWarranty
                Applications.
              </dd>

              <dt>“Documentation”</dt>
              <dd>
                means iWarranty’s online user guides, help and training material
                for the applicable Services, accessible via help.iwarranty.co or
                login to the applicable Services, as updated from time to time,
                including without limitation any notices and licence information,
                external usage policies, and security, privacy and architecture
                documentation for the applicable Services accessible through via
                iwarranty.co or any successor website, as updated from time to
                time or on login to the applicable Service.
              </dd>

              <dt>“iWarranty”</dt>
              <dd>
                means iWarranty Limited, a U.K. corporation having its principal
                place of business at suite 834, 19-21 Crawford Street, London W1H
                1PJ, United Kingdom.
              </dd>

              <dt>“iWarranty Platform”</dt>
              <dd>
                means iWarranty’s mobile and web-based applications (the “Apps”),
                the iWarranty website (including https://iwarranty.co) (the
                “Sites”) and related services, information and communications are
                collectively referred to as the “iWarranty Platform.”
              </dd>

              <dt>“Malicious Code”</dt>
              <dd>
                means code, files, scripts, agents or programs intended to do
                harm, including, for example, viruses, worms, time bombs and
                Trojan horses.
              </dd>

              <dt>“Non-iWarranty Application”</dt>
              <dd>
                means a Web-based or offline software application that is
                provided by the Customer or a third party and interoperates with
                a Service, including, for example, an application that is
                developed by or for Customer.
              </dd>

              <dt>“Order Form”</dt>
              <dd>
                means an ordering or other document specifying the Services to be
                provided hereunder that is entered into between Customer and
                iWarranty, including any addenda, pilot agreements and
                supplements. By entering into an Order Form or using the
                Services, an Affiliate agrees to be bound by the terms of this
                Agreement as if it were an original party hereto.
              </dd>

              <dt>“Products”</dt>
              <dd>
                means any and all of your products including any repairs,
                replacements, spare parts or new products.
              </dd>

              <dt>“Product Registration”</dt>
              <dd>
                means the registration of a Product in iWarranty Platform by a
                consumer or one of your Users.
              </dd>

              <dt>“Product Specific Terms”</dt>
              <dd>
                means standard terms from iWarranty that apply specifically to
                one or more particular Service, as set out in the applicable
                order from between the Customer and iWarranty.
              </dd>

              <dt>“Services”</dt>
              <dd>
                means the products and services that are ordered by Customer
                under an Order Form and made available online by iWarranty,
                including associated offline components, as described in the
                Documentation. “Services” exclude Content and Non-iWarranty
                Applications.
              </dd>

              <dt>“User”</dt>
              <dd>
                means, in the case of an individual accepting these terms on his
                or her own behalf, such individual, or, in the case of an
                individual accepting this Agreement on behalf of a company or
                other legal entity, an individual who is authorised by Customer
                to use a Service, for whom Customer has ordered the Service, and
                to whom Customer (or iWarranty at Customer’s request) has
                supplied a user identification and password. Users may include,
                for example, employees, consultants, contractors and agents of
                Customer, and third parties with which Customer transacts
                business.
              </dd>

              <dt>“we”, “us” or “our”</dt>
              <dd>
                refers to iWarranty Limited, a limited company incorporated in
                England (number 12012798) whose registered office is at Suite
                834, 19-21 Crawford Street, London W1H 1PJ.
              </dd>

              <dt>“you”, “your”</dt>
              <dd>means the party or parties to the agreement excluding us.</dd>
            </dl>
          </li>
        </ol>
      </div>
    </div>
  );
};

export function PlatformServicesAgreementSmartView() {
  const location = useLocation();

  // If someone visits the legacy route, default to showing the legacy version expanded.
  const defaultLegacyOpen =
    location.pathname === SAGE_ROUTE_NAME.PLATFORM_SERVICES_AGREEMENT;

  return (
    <div className="flex flex-grow flex-col justify-start">
      <PlatformServicesAgreementView1 />
    </div>
  );
}

export default PlatformServicesAgreementView;
